import { apiFetch } from './apiFetch'

export interface Complaint {
  id: number
  caseNumber: string
  filedBy: string
  /** The party complained against -- a person, an animal, an object, a condition. */
  respondent: string
  complaintText: string
  /** RECEIVED until the department rules; RESOLVED once it has. */
  status: string
  /**
   * The department's answer, or null if it has not ruled yet. On the public
   * record: a complainant holding a case number may read the answer to their
   * own complaint.
   */
  ruling: string | null
  ruledAt: number | null
  /** When notice reached the complainant, or null if nobody could be told. */
  noticeSentAt: number | null
  /** Estimated review time as quoted at filing, e.g. "3 rotations of Venus". */
  reviewEstimate: string
  createdAt: number
  updatedAt: number
}

export const NAME_LIMIT = 120
export const RESPONDENT_LIMIT = 120
export const EMAIL_LIMIT = 254
export const GRIEVANCE_LIMIT = 5000

/**
 * `complainantEmail` is optional: a child without an address must still be able
 * to file. Blank is sent as undefined rather than "", so the department has no
 * address on file rather than an empty one.
 */
export function fileComplaint(
  filedBy: string,
  respondent: string,
  complaintText: string,
  complainantEmail: string,
): Promise<Complaint> {
  return apiFetch<Complaint>('/complaints', {
    method: 'POST',
    body: JSON.stringify({
      filedBy,
      respondent,
      complaintText,
      complainantEmail: complainantEmail.trim() || undefined,
    }),
  })
}

/**
 * The unlisted docket. This path is the only thing keeping every complaint
 * private, and the page that shows it lives at the same path.
 */
export const DOCKET_PATH = '/secret/link/noone/knows/about'

export function listComplaints(): Promise<Complaint[]> {
  return apiFetch<Complaint[]>(DOCKET_PATH)
}

export function getComplaint(caseNumber: string): Promise<Complaint> {
  return apiFetch<Complaint>(`/complaints/${encodeURIComponent(caseNumber)}`)
}

/** "1 September 2026 at 14:05", for a record kept to the minute. */
export function officialTimestamp(millis: number): string {
  const time = new Date(millis).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })
  return `${officialDate(millis)} at ${time}`
}

/** "1 September 2026" -- the department does not abbreviate months. */
export function officialDate(millis: number): string {
  return new Date(millis).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}
